import { useEffect, useRef } from "react";
import { T } from "../app/tokens.js";

/** Owners retain selection, URL state and guarded transitions. Keyboard handling
 * is local to tab buttons, never the record's editors or inputs. */
export function RecordSectionTabs({ tabs, active, onChange, label = "Record sections",
  testId = "record-section-tabs", tabId, panelId, tabTestId }) {
  const refs = useRef({});
  const keyFocus = useRef(null);
  useEffect(() => {
    if (keyFocus.current === active) {
      refs.current[active]?.focus();
      keyFocus.current = null;
    }
  }, [active]);
  const select = (key) => onChange?.(key);
  return (
    <div data-testid={testId} data-record-section-tabs role="tablist" aria-label={label}
      style={{ position: "sticky", top: 0, zIndex: 3, margin: "0 -22px", padding: "0 22px",
        display: "flex", gap: 24, background: T.card, borderBottom: `1px solid ${T.border}`, overflowX: "auto" }}>
      <style>{`
        .record-section-tab { border: 0; border-bottom: 3px solid transparent; background: transparent; padding: 14px 0 11px; color: ${T.tabInactiveFg}; font: inherit; font-size: ${T.fsMd}px; font-weight: ${T.fwBold}; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
        .record-section-tab[aria-selected="true"] { border-bottom-color: ${T.editorBlue}; color: ${T.editorBlue}; }
        .record-section-tab:focus-visible { outline: 2px solid ${T.blue}; outline-offset: -2px; }
      `}</style>
      {tabs.filter(t => !t.hidden).map((t, i, available) => (
        <button key={t.key} ref={el => { refs.current[t.key] = el; }} type="button" role="tab"
          className="record-section-tab" id={tabId(t.key)} aria-controls={panelId(t.key)}
          aria-selected={active === t.key} tabIndex={active === t.key ? 0 : -1}
          data-testid={tabTestId?.(t.key)} onClick={() => select(t.key)}
          onKeyDown={e => {
            if (e.target !== e.currentTarget || e.altKey || e.ctrlKey || e.metaKey) return;
            const n = e.key === "ArrowRight" ? available[(i + 1) % available.length]
              : e.key === "ArrowLeft" ? available[(i + available.length - 1) % available.length]
              : e.key === "Home" ? available[0] : e.key === "End" ? available.at(-1) : null;
            if (!n) return;
            e.preventDefault(); e.stopPropagation();
            keyFocus.current = n.key;
            select(n.key);
          }}>
          {t.label}
          {Number.isInteger(t.count) && t.count > 0 && <span data-testid={`${tabTestId?.(t.key) ?? t.key}-count`}
            style={{ borderRadius: 999, padding: "0 6px", fontSize: T.fsXs, background: active === t.key ? T.tabBadgeActiveBg : T.tabBadgeInactiveBg,
              color: active === t.key ? T.editorBlue : T.tabBadgeInactiveFg }}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}
